import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Filter, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { ProductCard } from '../components/ProductCard';
import { QuickViewModal } from '../components/QuickViewModal';
import { RecentlyViewed } from '../components/RecentlyViewed';
import { useShopFilters } from '../hooks/useShopFilters';
import { useProductFilters, FilterPanel, FilterChip, useSavedFilters, SavedFiltersPills, SaveFilterButton } from '../components/ProductFilters';
import { trackViewItemList, trackSearch } from '../lib/analytics';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

import { TrustStrip } from '../components/TrustStrip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRODUCTS_PER_PAGE = 24;

export const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  const { brands: BRANDS } = useShopFilters();
  const { saved, save: saveFilter, remove: removeFilter } = useSavedFilters();

  const applySavedFilter = (params) => {
    setSearchParams(new URLSearchParams(params));
  };

  // Build current filter string for saving (exclude sort and page)
  const currentFilterString = useMemo(() => {
    const p = new URLSearchParams(searchParams);
    p.delete('sort');
    p.delete('page');
    return p.toString();
  }, [searchParams]);

  // Pagination
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const setPage = (p) => {
    const np = new URLSearchParams(searchParams);
    if (p <= 1) np.delete('page'); else np.set('page', String(p));
    setSearchParams(np);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // URL-driven filter state
  const selectedBrands = (searchParams.get('brand') || '').split(',').filter(Boolean);
  const selectedCategories = (searchParams.get('category') || '').split(',').filter(Boolean);
  const selectedSizes = (searchParams.get('size') || '').split(',').filter(Boolean);
  const selectedColors = (searchParams.get('color') || '').split(',').filter(Boolean);
  const selectedPriceRanges = (searchParams.get('price') || '').split(',').filter(Boolean);
  const inStockOnly = searchParams.get('in_stock') === 'true';
  const onSaleFilter = searchParams.get('on_sale') === 'true';
  const selectedDepartment = searchParams.get('department') || '';
  const search = searchParams.get('search') || '';
  const sortBy = searchParams.get('sort') || 'newest';

  // Fetch all products (backend filters: brand, category, department, search, on_sale)
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (selectedBrands.length) params.append('brand', selectedBrands.join(','));
        if (selectedCategories.length) params.append('category', selectedCategories.join(','));
        if (selectedDepartment) params.append('department', selectedDepartment);
        if (onSaleFilter) params.append('on_sale', 'true');
        if (search) params.append('search', search);
        const res = await axios.get(`${API}/products?limit=1000&view=grid&${params.toString()}`);
        setAllProducts(res.data);
        trackViewItemList(res.data, selectedDepartment || 'Shop');
        if (search) trackSearch(search);
      } catch {
        toast.error('Failed to fetch products');
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrands.join(','), selectedCategories.join(','), selectedDepartment, onSaleFilter, search]);

  // Client-side filtering engine
  const urlFilters = useMemo(() => ({
    sizes: selectedSizes,
    colors: selectedColors,
    priceRanges: selectedPriceRanges,
    inStockOnly,
  }), [selectedSizes.join(','), selectedColors.join(','), selectedPriceRanges.join(','), inStockOnly]);

  const { filtered: filteredProducts, filterOptions } = useProductFilters(allProducts, urlFilters);

  // Sort
  const sortedProducts = useMemo(() => {
    const sorted = [...filteredProducts];
    if (sortBy === 'price-low') sorted.sort((a, b) => a.price - b.price);
    else if (sortBy === 'price-high') sorted.sort((a, b) => b.price - a.price);
    else if (sortBy === 'name') sorted.sort((a, b) => a.name.localeCompare(b.name));
    return sorted;
  }, [filteredProducts, sortBy]);

  // URL param helpers
  const setMulti = (key, vals) => {
    const np = new URLSearchParams(searchParams);
    vals.length ? np.set(key, vals.join(',')) : np.delete(key);
    np.delete('page');
    setSearchParams(np);
  };

  const removeOne = (key, val) => {
    const cur = (searchParams.get(key) || '').split(',').filter(Boolean);
    setMulti(key, cur.filter(v => v !== val));
  };

  const setBool = (key, val) => {
    const np = new URLSearchParams(searchParams);
    val ? np.set(key, 'true') : np.delete(key);
    np.delete('page');
    setSearchParams(np);
  };

  const clearFilters = () => {
    const np = new URLSearchParams();
    if (sortBy !== 'newest') np.set('sort', sortBy);
    setSearchParams(np);
  };

  const removeDepartment = () => {
    const np = new URLSearchParams(searchParams);
    np.delete('department');
    setSearchParams(np);
  };

  // Active filter count
  const activeCount = selectedBrands.length + selectedCategories.length + selectedSizes.length +
    selectedColors.length + selectedPriceRanges.length + (inStockOnly ? 1 : 0) +
    (onSaleFilter ? 1 : 0) + (selectedDepartment ? 1 : 0) + (search ? 1 : 0);

  // Page title
  const getPageTitle = () => {
    if (onSaleFilter) return 'Sale';
    if (search) return `Search: "${search}"`;
    if (selectedDepartment) return selectedDepartment;
    if (selectedBrands.length === 1) {
      const b = BRANDS.find(b => b.id === selectedBrands[0]);
      return b?.name || selectedBrands[0];
    }
    if (selectedCategories.length === 1 && !selectedBrands.length) {
      return selectedCategories[0];
    }
    return 'All Products';
  };

  const currentBrand = selectedBrands.length === 1 ? BRANDS.find(b => b.id === selectedBrands[0]) : null;

  // Grouped view
  const groupedProducts = useMemo(() => {
    if (sortedProducts.length === 0) return null;
    if (selectedBrands.length !== 1 && !selectedDepartment) return null;
    const gk = selectedDepartment ? 'brand' : 'category';
    const groups = {};
    sortedProducts.forEach(p => {
      const key = p[gk] || 'Other';
      if (!groups[key]) groups[key] = [];
      groups[key].push(p);
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [selectedBrands.join(','), selectedDepartment, sortedProducts]);

  // Pagination
  const totalPages = groupedProducts ? 1 : Math.ceil(sortedProducts.length / PRODUCTS_PER_PAGE);
  const paginatedProducts = useMemo(() => {
    if (groupedProducts) return sortedProducts;
    const start = (currentPage - 1) * PRODUCTS_PER_PAGE;
    return sortedProducts.slice(start, start + PRODUCTS_PER_PAGE);
  }, [sortedProducts, currentPage, groupedProducts]);

  // Active chips
  const ActiveChips = ({ prefix = '' }) => {
    if (activeCount === 0) return null;
    return (
      <div className="flex flex-wrap gap-1.5" data-testid={`${prefix}active-filters`}>
        {onSaleFilter && <FilterChip label="On Sale" onRemove={() => setBool('on_sale', false)} testId={`${prefix}active-on-sale`} />}
        {inStockOnly && <FilterChip label="In Stock Only" onRemove={() => setBool('in_stock', false)} testId={`${prefix}active-in-stock`} />}
        {selectedDepartment && <FilterChip label={selectedDepartment} onRemove={removeDepartment} testId={`${prefix}active-department`} />}
        {selectedBrands.map(id => <FilterChip key={`b-${id}`} label={id} onRemove={() => removeOne('brand', id)} testId={`${prefix}active-brand-${id}`} />)}
        {selectedCategories.map(id => <FilterChip key={`c-${id}`} label={id} onRemove={() => removeOne('category', id)} testId={`${prefix}active-category-${id}`} />)}
        {selectedSizes.map(id => <FilterChip key={`s-${id}`} label={`Size: ${id}`} onRemove={() => removeOne('size', id)} testId={`${prefix}active-size-${id}`} />)}
        {selectedColors.map(id => <FilterChip key={`cl-${id}`} label={id} colour={id} onRemove={() => removeOne('color', id)} testId={`${prefix}active-color-${id}`} />)}
        {selectedPriceRanges.map(id => <FilterChip key={`p-${id}`} label={id.replace('-', '–').replace('+', '+')} onRemove={() => removeOne('price', id)} testId={`${prefix}active-price-${id}`} />)}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F7F7EB]">
      <TrustStrip />
      {/* Header */}
      <div className="bg-white border-b border-[#E5D8D0]">
        <div className="px-6 md:px-12 lg:px-24 py-6 md:py-12">
          <p className="text-sm tracking-[0.2em] text-[#DEA193] mb-2">Shop</p>
          <div className="flex items-center gap-4">
            {currentBrand?.logo_url && (
              <img src={currentBrand.logo_url} alt={currentBrand.name} className="h-12 md:h-16 w-auto object-contain" data-testid="brand-page-logo" />
            )}
            <h1 className="text-3xl sm:text-4xl md:text-5xl tracking-tight text-[#000000]" data-testid="shop-title">
              {getPageTitle()}
            </h1>
          </div>
          {!loading && (
            <p className="text-[#737373] mt-2" data-testid="product-count">
              {sortedProducts.length} product{sortedProducts.length !== 1 ? 's' : ''}
              {activeCount > 0 && allProducts.length !== sortedProducts.length && ` of ${allProducts.length}`}
            </p>
          )}
        </div>
      </div>

      <div className="px-6 md:px-12 lg:px-24 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar — Desktop */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-28">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-sm tracking-[0.15em] font-medium">Filters</h3>
                {activeCount > 0 && (
                  <button onClick={clearFilters} className="text-sm text-[#DEA193] hover:underline" data-testid="clear-filters">
                    Clear all
                  </button>
                )}
              </div>
              <SavedFiltersPills saved={saved} onApply={applySavedFilter} onRemove={removeFilter} />
              <FilterPanel
                filterOptions={filterOptions}
                selectedBrands={selectedBrands}
                onBrandsChange={(v) => setMulti('brand', v)}
                selectedCategories={selectedCategories}
                onCategoriesChange={(v) => setMulti('category', v)}
                selectedSizes={selectedSizes}
                onSizesChange={(v) => setMulti('size', v)}
                selectedColors={selectedColors}
                onColorsChange={(v) => setMulti('color', v)}
                selectedPriceRanges={selectedPriceRanges}
                onPriceRangesChange={(v) => setMulti('price', v)}
                inStockOnly={inStockOnly}
                onInStockChange={(v) => setBool('in_stock', v)}
              />
              <div className="mt-4">
                <ActiveChips />
                {activeCount > 0 && <SaveFilterButton currentParams={currentFilterString} onSave={saveFilter} />}
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1">
            {/* Sort + Mobile filter toggle */}
            <div className="flex items-center justify-between mb-6 lg:mb-8">
              <button
                onClick={() => setFiltersOpen(true)}
                className="lg:hidden flex items-center gap-2 px-4 py-2.5 border border-[#E5D8D0] text-sm bg-white"
                data-testid="mobile-filters-toggle"
              >
                <Filter className="w-4 h-4" />
                Filter & Sort {activeCount > 0 && `(${activeCount})`}
              </button>

              <div className="flex items-center gap-3 ml-auto">
                <span className="text-sm text-[#737373] hidden sm:block">Sort by:</span>
                <Select value={sortBy} onValueChange={(value) => {
                  const np = new URLSearchParams(searchParams);
                  np.set('sort', value);
                  setSearchParams(np);
                }}>
                  <SelectTrigger className="w-[160px] border-[#E5D8D0]" data-testid="sort-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="newest">Newest</SelectItem>
                    <SelectItem value="price-low">Price: Low to High</SelectItem>
                    <SelectItem value="price-high">Price: High to Low</SelectItem>
                    <SelectItem value="name">Name</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Products */}
            {loading ? (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="products-loading">
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <div key={i} className="animate-pulse">
                    <div className="aspect-[4/5] bg-[#E5D8D0] mb-4" />
                    <div className="h-3 bg-[#E5D8D0] w-1/3 mb-2" />
                    <div className="h-5 bg-[#E5D8D0] w-2/3 mb-2" />
                    <div className="h-4 bg-[#E5D8D0] w-1/4" />
                  </div>
                ))}
              </div>
            ) : sortedProducts.length > 0 ? (
              groupedProducts ? (
                <div className="space-y-12" data-testid="products-grouped">
                  {groupedProducts.map(([name, prods]) => (
                    <div key={name}>
                      <h2 className="text-lg font-medium tracking-[0.08em] text-[#000] border-b border-[#E5D8D0] pb-3 mb-6" data-testid={`category-group-${name}`}>
                        {name}
                        <span className="text-sm text-[#999] font-normal ml-2">({prods.length})</span>
                      </h2>
                      <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                        {prods.map((product, index) => (
                          <div key={product.id} className="animate-fade-in-up" style={{ animationDelay: `${index * 50}ms` }}>
                            <ProductCard product={product} onQuickView={setQuickViewProduct} />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="products-grid">
                    {paginatedProducts.map((product, index) => (
                      <div key={product.id} className="animate-fade-in-up" style={{ animationDelay: `${index * 50}ms` }}>
                        <ProductCard product={product} onQuickView={setQuickViewProduct} />
                      </div>
                    ))}
                  </div>
                  {totalPages > 1 && (
                    <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setPage} totalProducts={sortedProducts.length} />
                  )}
                </>
              )
            ) : (
              <div className="text-center py-20" data-testid="no-products">
                <p className="text-[#737373] text-lg">No products match your filters</p>
                <button onClick={clearFilters} className="mt-4 text-[#DEA193] hover:underline">
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Filters Drawer */}
      {filtersOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setFiltersOpen(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-[320px] max-w-[85vw] bg-white flex flex-col animate-in slide-in-from-right duration-200">
            <div className="flex items-center justify-between p-5 border-b border-[#E5D8D0]">
              <h3 className="text-lg font-medium">Filter & Sort</h3>
              <button onClick={() => setFiltersOpen(false)} className="p-2" data-testid="close-mobile-filters" aria-label="Close filters">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {/* Sort in mobile drawer */}
              <div className="mb-5">
                <p className="text-xs uppercase tracking-[0.15em] text-[#999] mb-2 font-medium">Sort by</p>
                <Select value={sortBy} onValueChange={(value) => {
                  const np = new URLSearchParams(searchParams);
                  np.set('sort', value);
                  setSearchParams(np);
                }}>
                  <SelectTrigger className="w-full border-[#E5D8D0]" data-testid="mobile-sort-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="newest">Newest</SelectItem>
                    <SelectItem value="price-low">Price: Low to High</SelectItem>
                    <SelectItem value="price-high">Price: High to Low</SelectItem>
                    <SelectItem value="name">Name</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <SavedFiltersPills saved={saved} onApply={applySavedFilter} onRemove={removeFilter} />
              <FilterPanel
                filterOptions={filterOptions}
                selectedBrands={selectedBrands}
                onBrandsChange={(v) => setMulti('brand', v)}
                selectedCategories={selectedCategories}
                onCategoriesChange={(v) => setMulti('category', v)}
                selectedSizes={selectedSizes}
                onSizesChange={(v) => setMulti('size', v)}
                selectedColors={selectedColors}
                onColorsChange={(v) => setMulti('color', v)}
                selectedPriceRanges={selectedPriceRanges}
                onPriceRangesChange={(v) => setMulti('price', v)}
                inStockOnly={inStockOnly}
                onInStockChange={(v) => setBool('in_stock', v)}
                mobile
              />
              <div className="mt-4">
                <ActiveChips prefix="mobile-" />
                {activeCount > 0 && <SaveFilterButton currentParams={currentFilterString} onSave={saveFilter} />}
              </div>
            </div>
            <div className="p-5 border-t border-[#E5D8D0] flex gap-3">
              <button onClick={clearFilters} className="flex-1 py-2.5 border border-[#E5D8D0] text-sm" data-testid="mobile-clear-filters">Clear All</button>
              <button onClick={() => setFiltersOpen(false)} className="flex-1 py-2.5 bg-[#111] text-white text-sm" data-testid="mobile-apply-filters">
                Show {sortedProducts.length} Results
              </button>
            </div>
          </div>
        </div>
      )}

      {quickViewProduct && <QuickViewModal product={quickViewProduct} onClose={() => setQuickViewProduct(null)} />}
      <RecentlyViewed limit={8} />
    </div>
  );
};

/** Pagination Controls */
const Pagination = ({ currentPage, totalPages, onPageChange, totalProducts }) => {
  const pages = [];
  const maxVis = 5;
  let start = Math.max(1, currentPage - Math.floor(maxVis / 2));
  let end = Math.min(totalPages, start + maxVis - 1);
  if (end - start + 1 < maxVis) start = Math.max(1, end - maxVis + 1);
  for (let i = start; i <= end; i++) pages.push(i);

  return (
    <div className="flex items-center justify-center gap-2 mt-12 pt-8 border-t border-[#E5D8D0]" data-testid="pagination">
      <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage <= 1}
        className="p-2 border border-[#E5D8D0] hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        data-testid="pagination-prev" aria-label="Previous page">
        <ChevronLeft className="w-4 h-4" />
      </button>
      {start > 1 && (
        <>
          <button onClick={() => onPageChange(1)} className="w-10 h-10 text-sm border border-[#E5D8D0] hover:bg-white transition-colors" data-testid="pagination-page-1">1</button>
          {start > 2 && <span className="text-[#999] text-sm px-1">...</span>}
        </>
      )}
      {pages.map(p => (
        <button key={p} onClick={() => onPageChange(p)}
          className={`w-10 h-10 text-sm transition-colors ${p === currentPage ? 'bg-[#000] text-white border border-[#000]' : 'border border-[#E5D8D0] hover:bg-white'}`}
          data-testid={`pagination-page-${p}`} aria-current={p === currentPage ? 'page' : undefined}>
          {p}
        </button>
      ))}
      {end < totalPages && (
        <>
          {end < totalPages - 1 && <span className="text-[#999] text-sm px-1">...</span>}
          <button onClick={() => onPageChange(totalPages)} className="w-10 h-10 text-sm border border-[#E5D8D0] hover:bg-white transition-colors" data-testid={`pagination-page-${totalPages}`}>{totalPages}</button>
        </>
      )}
      <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage >= totalPages}
        className="p-2 border border-[#E5D8D0] hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        data-testid="pagination-next" aria-label="Next page">
        <ChevronRight className="w-4 h-4" />
      </button>
      <span className="text-xs text-[#999] ml-3">{totalProducts} products</span>
    </div>
  );
};
